import React, {HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Autocomplete, Box, Button, Grid, Popover, Stack, TextField, Typography} from "@mui/material";
import dayjs, {Dayjs} from "dayjs";
import {autocompleteChangeEvent, SetState} from "../../util/FormHandlers";
import {DateField} from "../date-field/DateField";
import {useToday} from "../../util/DateUtil";
import {Opt} from "../../util/Optional";

export interface DateRangeOption {
    id: string;
    label: string;

    fromDate?: (now: Dayjs) => Dayjs;
    toDate?: (now: Dayjs) => Dayjs;
}

function option(
    id: string,
    label: string,
    fromDate?: (now: Dayjs) => Dayjs,
    toDate?: (now: Dayjs) => Dayjs,
): DateRangeOption {
    return {
        id,
        label,
        fromDate,
        toDate,
    };
}

function asString(date?: Dayjs | ((now: Dayjs) => Dayjs)): string | undefined {
    const d = typeof date === 'function' ? date(dayjs()) : date;
    return d?.format?.("YYYY-MM-DD");
}

export function asDates(dateRange: Opt<DateRangeOption>): {from: string | undefined, to: string | undefined} {
    return {
        from: asString(dateRange?.fromDate),
        to: asString(dateRange?.toDate),
    };
}

function dateRange(
    fromDate?: Dayjs | ((now: Dayjs) => Dayjs),
    toDate?: Dayjs | ((now: Dayjs) => Dayjs)
): string {
    const from = asString(fromDate);
    const to = asString(toDate);

    return from && to ? (
        `${from}–${to}`
    ) : from ? (
        `Since ${from}`
    ) : to ? (
        `Until ${to}`
    ) : '';
}

export const DateRangeOptions = {
    thisMonth: option('this-month', 'This Month',
        now => now.startOf('month')
    ),
    lastMonth: option('last-month', 'Last Month',
        now => now.subtract(1, 'month').startOf('month'),
        now => now.subtract(1, 'month').endOf('month'),
    ),
    monthRegex: /month-(\d+)-(\d+)/i,
    month(month: Dayjs): DateRangeOption {
        return option(`month-${month.year()}-${month.month()}`, month.format('MMMM, YYYY'),
            _ => month.startOf('month'),
            _ => month.endOf('month'),
        );
    },
    monthsBefore(refMonth: Dayjs): DateRangeOption[] {
        return Array.from({length: refMonth.month() % 12}, (_, monthIndex) => {
            const month = refMonth.subtract(monthIndex + 1, 'month');
            return DateRangeOptions.month(month);
        });
    },
    lastThreeMonths: option('last-three-months', 'Last Three Months',
        now => now.subtract(2, 'month').startOf('month'),
    ),
    lastFourMonths: option('last-four-months', 'Last Four Months',
        now => now.subtract(3, 'month').startOf('month'),
    ),
    lastSixMonths: option('last-six-months', 'Last Six Months',
        now => now.subtract(5, 'month').startOf('month'),
    ),
    thisYear: option('this-year', 'This Year',
        now => now.startOf('year'),
    ),
    lastYear: option('last-year', 'Last Year',
        now => now.subtract(1, 'year').startOf('year'),
        now => now.subtract(1, 'year').endOf('year'),
    ),
    custom: option('custom', 'Custom'),
    dateRangeRegex: /((\d{4}-\d{2}-\d{2})|\*)-((\d{4}-\d{2}-\d{2})|\*)/,
    dateRange(fromDate?: Dayjs, toDate?: Dayjs): DateRangeOption {
        return {
            id: `${fromDate ? fromDate.format('YYYY-MM-DD') : '*'}-${toDate ? toDate.format('YYYY-MM-DD') : '*'}`,
            label: dateRange(fromDate, toDate),
            fromDate: fromDate && (() => fromDate),
            toDate: toDate && (() => toDate),
        };
    },
    encode(option: DateRangeOption): string {
        return option.id;
    },
    decode(value: string): DateRangeOption | undefined {
        const now = dayjs();
        const monthMatch = value.match(DateRangeOptions.monthRegex);
        const dateRangeMatch = value.match(DateRangeOptions.dateRangeRegex);

        return value === DateRangeOptions.thisMonth.id ? (
            DateRangeOptions.thisMonth
        ) : value === DateRangeOptions.lastMonth.id ? (
            DateRangeOptions.lastMonth
        ) : value === DateRangeOptions.lastThreeMonths.id ? (
            DateRangeOptions.lastThreeMonths
        ) : value === DateRangeOptions.lastFourMonths.id ? (
            DateRangeOptions.lastFourMonths
        ) : value === DateRangeOptions.lastSixMonths.id ? (
            DateRangeOptions.lastSixMonths
        ) : value === DateRangeOptions.thisYear.id ? (
            DateRangeOptions.thisYear
        ) : value === DateRangeOptions.lastYear.id ? (
            DateRangeOptions.lastYear
        ) : monthMatch ? (
            DateRangeOptions.month(now.year(Number(monthMatch[1])).month(Number(monthMatch[2])))
        ) : dateRangeMatch ? (
            DateRangeOptions.dateRange(
                dateRangeMatch[2] ? dayjs(dateRangeMatch[2]) : undefined,
                dateRangeMatch[4] ? dayjs(dateRangeMatch[4]) : undefined,
            )
        ) : undefined;
    }
};


export function dateRangeOptions(now: Dayjs = dayjs()): DateRangeOption[] {
    const prevMonth = now.subtract(1, 'month');
    return [
        DateRangeOptions.thisMonth,
        DateRangeOptions.lastMonth,
        ...DateRangeOptions.monthsBefore(prevMonth),
        DateRangeOptions.lastThreeMonths,
        DateRangeOptions.lastFourMonths,
        DateRangeOptions.lastSixMonths,
        DateRangeOptions.thisYear,
        DateRangeOptions.lastYear,
        DateRangeOptions.custom,
    ];
}

function CustomDateRangePopover({anchorEl, open, onClose, value, onSet}: {
    anchorEl: HTMLElement | null,
    open: boolean,
    onClose: () => void,
    value: DateRangeOption | undefined,
    onSet: (range: DateRangeOption) => void,
}) {
    const [draftFromDate, setDraftFromDate] = useState(asString(value?.fromDate));
    const [draftToDate, setDraftToDate] = useState(asString(value?.toDate));

    useEffect(() => {
        if (open) {
            const {from, to} = asDates(value);
            setDraftFromDate(from);
            setDraftToDate(to);
        }
    }, [open, value]);

    const handleClick = useCallback(() => {
        onSet(DateRangeOptions.dateRange(
            draftFromDate ? dayjs(draftFromDate) : undefined,
            draftToDate ? dayjs(draftToDate) : undefined,
        ));
    }, [onSet, draftFromDate, draftToDate]);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        >
            <Stack gap={2} p={2}>
                <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                    <DateField
                        label="From"
                        value={draftFromDate}
                        setValue={setDraftFromDate}
                        slotProps={{field: {clearable: true}}}
                    />
                    <DateField
                        label="To"
                        value={draftToDate}
                        setValue={setDraftToDate}
                        slotProps={{field: {clearable: true}}}
                    />
                </Stack>
                <Button
                    variant="contained"
                    onClick={handleClick}
                    sx={{alignSelf: 'flex-end'}}
                >
                    Set
                </Button>
            </Stack>
        </Popover>
    );
}

const renderOption = (props: HTMLAttributes<HTMLLIElement> & { key: React.Key }, option: DateRangeOption) => (
    <Box {...props} key={props.key} component="li">
        <Stack direction="row" justifyContent="space-between" gap={1} sx={{width: '100%'}}>
            <Typography variant="body1">{option.label}</Typography>
            <Typography variant="body2" color="text.secondary">
                {dateRange(option?.fromDate, option?.toDate)}
            </Typography>
        </Stack>
    </Box>
);

export function DateRangeFilter<Filter extends { [P in PropertyName]?: DateRangeOption | undefined }, PropertyName extends keyof Filter>(
    {filter, setFilter, label = 'Date range', property}: {
        filter: Filter,
        setFilter: SetState<Filter>,
        label?: string;
        property: PropertyName;
    }
) {
    const currentDate = useToday();
    const now = useMemo(() => dayjs(currentDate), [currentDate]);
    const options = useMemo(() => dateRangeOptions(now), [now]);
    const anchorRef = useRef<HTMLDivElement>(null);
    const [customOpen, setCustomOpen] = useState(false);

    const handleChange = useCallback(autocompleteChangeEvent<DateRangeOption | null>(value => {
        if (value?.id === DateRangeOptions.custom.id) {
            setCustomOpen(true);
        } else {
            setCustomOpen(false);
            setFilter(filter => ({...filter, [property]: value ?? undefined}));
        }
    }), [setFilter, setCustomOpen]);

    return (
        <Grid size={{md: 3, xs: 12}} minWidth={220}>
            <div ref={anchorRef}>
                <Autocomplete
                    options={options}
                    value={customOpen ? DateRangeOptions.custom : (filter[property] ?? null)}
                    onChange={handleChange}
                    getOptionLabel={current => current.label}
                    isOptionEqualToValue={(left, right) => left.id === right.id}
                    renderInput={params => <TextField {...params} label={label}/>}
                    renderOption={renderOption}
                    slotProps={{
                        popper: {
                            sx: {
                                minWidth: '400px'
                            }
                        }
                    }}
                />
            </div>
            <CustomDateRangePopover
                open={customOpen}
                anchorEl={anchorRef.current}
                onClose={() => setCustomOpen(false)}
                value={filter[property]}
                onSet={value => {
                    setFilter(filter => ({...filter, [property]: value}));
                    setCustomOpen(false);
                }}
            />
        </Grid>
    );
}
